import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type { InsertControlTestOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertControlTestOversightResultInput = {
  OversightAssessmentId: string;
  ControlIds: string[];
  Title?: string | null;
  TestType?: TestType | null;
  Description: string;
  DesignEffectiveness?: number | null;
  PerformanceEffectiveness?: number | null;
  OverallEffectiveness?: number | null;
  Submitter: string;
  TestDate: string;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertControlTestOversightResultMutation => ({
  insertChildControlTestOversightResult: {
    __typename: 'insertControlTestResultOutput',
    Ids: trpcData.Ids,
  },
});

export const useInsertControlTestOversightResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.insertControlTestOversightResult.mutationOptions(
      {
        onSuccess: async () => {
          await Promise.all([
            // Apollo parity: evictField(cache, 'control_test_oversight_result')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.frontend.assessment.oversightResultById.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.testResult.controlTestOversightResultById.pathKey(),
            }),
            // Apollo parity: evictField(cache, 'control') — the control-scoped
            // oversight test-result reads.
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.testResult.oversightAssessmentTestResultsByControlId.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.testResult.latestOversightAssessmentTestResultsByControlId.pathKey(),
            }),
            // Aggregate/count families.
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
            }),
            // Apollo parity: evictField(cache, 'oversight_assessment')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityRegister.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityCount.queryKey(),
            }),
          ]);
        },
      }
    ),
  });

  return {
    insertControlTestOversightResult: async (
      variables: InsertControlTestOversightResultInput
    ): Promise<InsertControlTestOversightResultMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
